import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from '@insio/ui-toolkit';
import { navigation } from '@/navigation';
import {
  SwitchTargetKind,
  useAccountSwitchPrompt,
  useAuth,
  usePendingInvitations,
  useSignOut
} from '@/features/auth';
import { useMenuUsage } from '@/hooks/use-menu-usage';

/** Menu props describing the signed-in person — the same in the Tappeer app and the board. */
export type ProfileMenuProps = Pick<
  MenuProps,
  | 'user'
  | 'usage'
  | 'accounts'
  | 'invitations'
  | 'logout'
  | 'linkComponent'
  | 'myAccountHref'
  | 'onMyAccountClick'
  | 'policiesHref'
  | 'isProfileActive'
>;

export type AccountSwitchPrompt = ReturnType<typeof useAccountSwitchPrompt>;

/**
 * The profile half of the top menu: user, quota, account switcher (master accounts, then organizations),
 * invitations and sign-out.
 * The switch prompt is returned too, because its confirmation renders outside the menu.
 *
 * @param planHref - Where the quota panel's plan links to; omit to render it as plain text.
 */
export function useProfileMenu(planHref?: string): {
  profileMenuProps: ProfileMenuProps;
  switchPrompt: AccountSwitchPrompt;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useSignOut();
  const { user } = useAuth();
  const switchPrompt = useAccountSwitchPrompt();
  const { invitations, accept: acceptInvitation } = usePendingInvitations();
  const usage = useMenuUsage(planHref);

  const { accounts, activeAccountId, pendingAccountId, switching, requestSwitch } = switchPrompt;
  const userName = user?.displayName || user?.email || 'User';

  return {
    switchPrompt,
    profileMenuProps: {
      linkComponent: Link,
      myAccountHref: navigation.myAccount(),
      onMyAccountClick: () => navigate(navigation.myAccount()),
      policiesHref: navigation.policies(),
      isProfileActive: location.pathname.startsWith('/my-account'),
      user: { name: userName, email: user?.email ?? '' },
      usage,
      accounts:
        accounts.length > 0
          ? {
              activeId: activeAccountId ?? '',
              // Master accounts are pinned above the organizations, so the switcher reads as two groups.
              items: accounts.map(({ id, name, kind }) =>
                kind === SwitchTargetKind.MASTER_ACCOUNT
                  ? { id, name, icon: 'briefcase' as const, pinned: true }
                  : { id, name }
              ),
              onSwitch: requestSwitch,
              pendingId: pendingAccountId,
              switching
            }
          : null,
      invitations:
        invitations.length > 0 ? { items: invitations, onAccept: acceptInvitation } : null,
      logout: signOut
    }
  };
}
